/**
 * Web module registry — the single place that knows every module.
 * Module developers edit ONLY their own `src/modules/<code>/` folder; this file just imports them.
 */
import type { RouteObject } from 'react-router-dom';
import type { PermissionRequirement } from '@web/auth/permissions';
import type { ShellKind } from '@web/shells/detect';
import core from './core';
import iam from './iam';
import fin from './fin';
import tax from './tax';
import rpt from './rpt';
import bill from './bill';
import type { DashboardWidget, EssHomeCard, EssRequestType, MenuItem, OpsTasksWidget, WebModule } from './types';

export type { WebModule, MenuItem } from './types';

export const webModules: WebModule[] = [core, iam, bill, fin, tax, rpt]
  .map((m, i) => ({ m, i }))
  .sort((a, b) => (a.m.order ?? 100) - (b.m.order ?? 100) || a.i - b.i)
  .map(({ m }) => m);

type Can = (perm: PermissionRequirement | undefined) => boolean;

/** All routes contributed for a shell, in module order. */
export function routesFor(shell: ShellKind): RouteObject[] {
  return webModules.flatMap((m) => m.routes[shell] ?? []);
}

/** Removes entries the user may not see (and groups left empty). */
export function filterMenu(items: MenuItem[], can: Can): MenuItem[] {
  const out: MenuItem[] = [];
  for (const item of items) {
    if (!can(item.permission)) continue;
    const children = item.children ? filterMenu(item.children, can) : undefined;
    if (item.children && !item.path && (!children || children.length === 0)) continue;
    out.push({ ...item, children });
  }
  return out;
}

export interface ModuleMenu {
  module: WebModule;
  items: MenuItem[];
}

/** Admin sidebar: modules with at least one visible entry. */
export function adminMenu(can: Can): ModuleMenu[] {
  return webModules.map((module) => ({ module, items: filterMenu(module.menu, can) })).filter((g) => g.items.length > 0);
}

export function portalMenu(can: Can): MenuItem[] {
  return webModules.flatMap((m) => filterMenu(m.portalMenu ?? [], can));
}

export function opsMenu(can: Can): MenuItem[] {
  return webModules.flatMap((m) => filterMenu(m.opsMenu ?? [], can));
}

/** Flat list of navigable menu entries (used by global search). */
export function flattenMenu(items: MenuItem[]): MenuItem[] {
  return items.flatMap((i) => [...(i.path ? [i] : []), ...flattenMenu(i.children ?? [])]);
}

function byOrder<T extends { order?: number }>(a: T, b: T): number {
  return (a.order ?? 100) - (b.order ?? 100);
}

export function dashboardsFor(can: Can): DashboardWidget[] {
  return webModules.flatMap((m) => m.dashboards ?? []).filter((w) => can(w.permission)).sort(byOrder);
}

export function opsWidgetsFor(can: Can): OpsTasksWidget[] {
  return webModules
    .flatMap((m): OpsTasksWidget[] => {
      const w = m.opsTasksWidget;
      if (!w) return [];
      return Array.isArray(w) ? w : [{ key: m.code, component: w, order: m.order }];
    })
    .filter((w) => can(w.permission))
    .sort(byOrder);
}

export function essCardsFor(can: Can): EssHomeCard[] {
  return webModules
    .flatMap((m) =>
      (m.essHomeCards ?? []).map((c, i): EssHomeCard => ('component' in c ? c : { key: `${m.code}-${i}`, component: c, order: m.order })),
    )
    .filter((c) => can(c.permission))
    .sort(byOrder);
}

export function essRequestTypesFor(can: Can): EssRequestType[] {
  return webModules.flatMap((m) => m.essRequestTypes ?? []).filter((r) => can(r.permission));
}
